import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { Category } from "src/app/shared/models/category.model";
import { GiftCardsUriService } from "./gift-cards-uri.service";
import { environment } from "src/environments/environment";
import { PhysicalOrderList } from "../models/physical-order-list.model";
import { User } from "src/app/shared/models/user.model";
import { ReportParams } from "../models/report-params.model";
import { PhysicalCardStatus } from "../models/card-status.model";
import { PhysicalActivate } from "../models/physical-cards.model";

interface QueryParams {
  key: string;
  value: string;
}
const baseUrl = environment.baseUrl;

@Injectable({
  providedIn: "root",
}) 
export class GiftCardsGatewayService {
  constructor(
    private giftCardsUriService: GiftCardsUriService,
    private http: HttpClient
  ) { }

  getCategories(accessory: boolean = false): Observable<Category[]> {
    const url = this.giftCardsUriService.getCategoriesUri();

    let physical = environment.card_types.physical,
      accessories = environment.card_types.accessory,
      preActivatedBundle = environment.card_types.preActivatedBundle;

    let params = new HttpParams();
    let types = [physical, preActivatedBundle];
    params = params.append(
      "types",
      accessory ? accessories.toString() : types.join(",")
    );

    return this.http.get<Category[]>(url, { params });
  }

  getCategoriesForPhysical(filters): Observable<Category[]> {
    const url = this.giftCardsUriService.getCategoriesUri();
    let params = new HttpParams();

    for (const filter in filters) {
      if (filters[filter]) {
        params = params.append(filter, filters[filter]);
      }
    }

    return this.http.get<Category[]>(url, { params });
  }

  getProductList(
    limit: number,
    offset: number,
    query_params?: QueryParams[]
  ): Observable<any> {
    let params = new HttpParams();
    params = params.append("limit", limit.toString());
    params = params.append("offset", offset.toString());

    if (query_params) {
      for (let query of query_params) {
        params = params.append(query.key, query.value);
      }
    }

    const url = this.giftCardsUriService.getProductListUri();
    return this.http.get<any>(url, { params });
  }

  getAccessoriesProducts(
    limit: number,
    offset: number,
    query_params?: QueryParams[]
  ): Observable<any> {
    let params = new HttpParams();
    params = params.append("limit", limit.toString());
    params = params.append("offset", offset.toString());

    if (query_params) {
      for (let query of query_params) {
        params = params.append(query.key, query.value);
      }
    }

    const url = this.giftCardsUriService.getAccessoriesProductsUri();
    return this.http.get<any>(url, { params });
  }

  getAccessoryOrdersList(filters?) {
    let params = new HttpParams();

    for (let filter in filters) {
      params = params.append(filter, filters[filter]);
    }

    const url = this.giftCardsUriService.getAccessoryOrdersUri();
    return this.http.get<any>(url, { params });
  }

  getAccessoryOrderDetail(id) {
    const url = this.giftCardsUriService.getAccessoryOrderDetail(id);
    return this.http.get<any>(url);
  }

  createAccessoryOrder(data): Observable<any> {
    const url = this.giftCardsUriService.getAccessoryOrdersUri();
    return this.http.post<any>(url, data)
  }

  getPhysicalOrdersList(filters?): Observable<PhysicalOrderList> {
    let params = new HttpParams();

    for (let filter in filters) {
      params = params.append(filter, filters[filter]);
    }

    const url = this.giftCardsUriService.getPhysicalOrdersListUri();
    return this.http.get<PhysicalOrderList>(url, { params });
  }

  getPhysicalOrdersArchiveList(filters?): Observable<PhysicalOrderList> {
    let params = new HttpParams();

    for (let filter in filters) {
      params = params.append(filter, filters[filter]);
    }

    const url = this.giftCardsUriService.getPhysicalOrdersListArchiveUri();
    return this.http.get<PhysicalOrderList>(url, { params });
  }

  getUserDetail(userId: number): Observable<User> {
    const url = this.giftCardsUriService.getUserDetailUri(userId);
    return this.http.get<User>(url);
  }

  getReport(reportParams: ReportParams): Observable<any> {
    let params = new HttpParams();

    if (reportParams) {
      for (let param in reportParams) {
        if (reportParams[param]) {
          params = params.append(param, reportParams[param]);
        }
      }
    }
    const url = this.giftCardsUriService.getReportUri();
    return this.http.get<any>(url, { params });
  }

  getProductDetail(productId: number): Observable<any> {
    const url = this.giftCardsUriService.getProductDetailUri(productId);
    return this.http.get<any>(url);
  }

  getUserReport(reportParams: ReportParams): Observable<any> {
    let params = new HttpParams();
    if (reportParams) {
      for (let param in reportParams) {
        if (reportParams[param]) {
          params = params.append(param, reportParams[param]);
        }
      }
    }
    const url = this.giftCardsUriService.getUserReportUri();
    return this.http.get<any>(url, { params });
  }

  getOrders(limit: number, offset: number, filters?: any): Observable<any> {
    let params = new HttpParams();

    params = params.append("limit", limit.toString());
    params = params.append("offset", offset.toString());

    for (let filter in filters) {
      params = params.append(filter, filters[filter]);
    }

    const url = this.giftCardsUriService.getOrdersUri();
    return this.http.get<any>(url, { params });
  }

  getGiftCardStatus(serialNumber: number): Observable<PhysicalCardStatus> {
    const data = { "serial": serialNumber.toString() };
    const url = this.giftCardsUriService.getGiftCardStatusUri();

    return this.http.post<PhysicalCardStatus>(url, data);
  }

  activateGiftCard(
    serialNumber: number,
    price: number,
    pin?: number
  ): Observable<PhysicalActivate> {
    const data = {
      serial: serialNumber,
      price,
      pin,
      type: 1,
    };

    const url = this.giftCardsUriService.getActivateGiftCardUri();
    return this.http.post<any>(url, data);
  }

  activateBulkGiftCard(data): Observable<any> {
    // const url = this.giftCardsUriService.getActivateGiftCardUri();
    const url = this.giftCardsUriService.getBulkActivateGiftCardUri();
    return this.http.post(url, data);
  }

  createPhysicalOrder(data): Observable<any> {
    return this.http.post(`${baseUrl}/physical-market/order/`, data);
  }

  getPhysicalMarketOrdersList(filters?): Observable<PhysicalOrderList> {
    let params = new HttpParams();

    for (let filter in filters) {
      params = params.append(filter, filters[filter]);
    }

    return this.http.get<PhysicalOrderList>(
      `${baseUrl}/physical-market/order/`,
      { params }
    );
  }

  getSubscriptionOrderList(filters?): Observable<any> {
    let params = new HttpParams();
    const url = this.giftCardsUriService.getSubscriptionOrderUri();
    for (let filter in filters) {
      params = params.append(filter, filters[filter]);
    }
    return this.http.get(url, { params });
  }

  createSubscriptionOrder(data): Observable<any> {
    const url = this.giftCardsUriService.getSubscriptionOrderUri();

    return this.http.post(url, data);
  }

  getSubscriptionCardList(): Observable<any> {
    const url = this.giftCardsUriService.getSubscriptionCardsUri();

    return this.http.get(url);
  }

  getSubscriptionCustomerCards(filters?): Observable<any> {
    const url = this.giftCardsUriService.getSubscriptionCustomerCardsUri();
    let params = new HttpParams();
    for (let filter in filters) {
      params = params.append(filter, filters[filter]);
    }
    return this.http.get(url, { params });
  }

  getSubscriptionGatewayCustomerCard(id, filters?) {
    let params = new HttpParams();

    for (const filter in filters) {
      if (filters[filter]) {
        params = params.append(filter, filters[filter]);
      }
    }

    const url = this.giftCardsUriService.getSubscriptionGatewayCustomerCardUri(id);
    return this.http.get(url, { params })
  }
  
  getSubscriptionGateways(filters?): Observable<any> {
    const url = this.giftCardsUriService.getSubscriptionMethodUri();
    let params = new HttpParams();
    for (let filter in filters) {
      params = params.append(filter, filters[filter]);
    }
    return this.http.get(url, { params });
  }

  getSubscriptionPayments(filters): Observable<any> {
    const url = this.giftCardsUriService.getSubscriptionPaymentsUri();
    let params = new HttpParams();
    for (let filter in filters) {
      params = params.append(filter, filters[filter]);
    }
    return this.http.get(url, { params });
  }

  cancelSubscriptionOrder(id): Observable<any> {
    const url = this.giftCardsUriService.getSubscriptionOrderCancelUri(id);

    return this.http.put(url, {});
  }

  validateSubscriptionPayment(data): Observable<any> {
    const url = this.giftCardsUriService.getSubscriptionOrderValidationUri();

    return this.http.post(url, data);
  }

  validateAccessoryOrderPayment(data): Observable<any> {
    const url = this.giftCardsUriService.getAccessoryOrderValidationUri();

    return this.http.post(url, data);
  }

  validatePhysicalOrderPayment(data: any): Promise<any> {
    const url = `${baseUrl}/physical-market/validation/`;
    return new Promise((resolve, reject) => {
      this.http.post(url, data).subscribe(
        success => {
          data = {
            data: success
          }
          resolve('');
        },
        error => {
          data = error;
          reject();
        }
      )
    })
  }

  getPhysicalMarketGateways(): Observable<any> {
    const url = `${baseUrl}/physical-market/gateways/`;
    return this.http.get<any>(url);
  }

  retryPhysicalOrderPayment(orderId: number, data: any) {
    const url = `${baseUrl}/physical-market/retry-payment/${orderId}/`;
    return this.http.post<any>(url, data);
  }
}
